import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { supabase } from '../supabaseClient';
import ResilierAbonnement from './ResilierAbonnement';
import { LayoutDashboard, Users, CreditCard, User } from 'lucide-react';

const Header = () => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [token, setToken] = useState(null);
  const [creditsData, setCreditsData] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Charger l'abonnement de l'élève (pour la gestion/résiliation dans le menu)
  const loadCredits = useCallback(async (accessToken) => {
    if (!accessToken) return;
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/credits/me`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setCreditsData(res.data?.success ? res.data : null);
    } catch {
      setCreditsData(null);
    }
  }, []);

  // Pages où on ne montre PAS le header
  const hideOnPages = ['/login', '/professor-login', '/register', '/forgot-password', '/reset-password'];
  const shouldHide = hideOnPages.some(page => location.pathname.startsWith(page)) || 
                     location.pathname.startsWith('/cours/');

  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        // Déterminer le type d'utilisateur
        const role = session.user.user_metadata?.role;
        setUserType(role === 'prof' ? 'prof' : 'eleve');
        setToken(session.access_token);
        if (role !== 'prof') loadCredits(session.access_token);
      }
    };
    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setUser(session.user);
        const role = session.user.user_metadata?.role;
        setUserType(role === 'prof' ? 'prof' : 'eleve');
        setToken(session.access_token);
        if (role !== 'prof') loadCredits(session.access_token);
      } else {
        setUser(null);
        setUserType(null);
        setToken(null);
        setCreditsData(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [loadCredits]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  // Ouvre le portail Stripe (carte bancaire, factures) puis y redirige
  const openBillingPortal = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/stripe/billing-portal`,
        { return_url: window.location.href },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.url) window.location.href = res.data.url;
    } catch (e) {
      alert(e?.response?.data?.error || "Impossible d'ouvrir le portail de gestion.");
    }
  };

  if (shouldHide) return null;

  const isActive = (path) => location.pathname === path;

  return (
    <header style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      height: '60px',
      background: 'linear-gradient(135deg, #064e3b 0%, #065f46 100%)',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 20px'
    }}>
      {/* Logo — mark (alif ا + accent yaz ⵣ amazigh) + wordmark */}
      <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <svg width="30" height="30" viewBox="0 0 44 44" aria-hidden="true" style={{ display: 'block' }}>
          <rect x="1" y="1" width="42" height="42" rx="12" fill="#ffffff" />
          <rect x="18.75" y="11" width="5.5" height="22" rx="2.7" fill="#065f46" />
          <text x="31" y="34" textAnchor="middle" fontFamily="'Noto Sans Tifinagh', sans-serif" fontSize="12" fill="#c99a5b">ⵣ</text>
        </svg>
        <span style={{
          color: 'white',
          fontSize: '21px',
          fontWeight: 600,
          letterSpacing: '-0.4px',
          fontFamily: "'DM Sans', -apple-system, sans-serif"
        }}>
          firstarabic
        </span>
      </Link>

      {/* Navigation */}
      <nav style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {user ? (
          <>
            {/* Liens selon le type d'utilisateur */}
            {userType === 'eleve' && (
              <>
                <NavLink to="/dashboard" active={isActive('/dashboard')}>
                  <LayoutDashboard size={16} style={{ verticalAlign: -3, marginRight: 6 }} />Dashboard
                </NavLink>
                <NavLink to="/professeurs" active={isActive('/professeurs')}>
                  <Users size={16} style={{ verticalAlign: -3, marginRight: 6 }} />Professeurs
                </NavLink>
                <NavLink to="/abonnement" active={isActive('/abonnement')}>
                  <CreditCard size={16} style={{ verticalAlign: -3, marginRight: 6 }} />Abonnement
                </NavLink>
              </>
            )}
            {userType === 'prof' && (
              <>
                <NavLink to="/prof-dashboard" active={isActive('/prof-dashboard')}>
                  <LayoutDashboard size={16} style={{ verticalAlign: -3, marginRight: 6 }} />Dashboard
                </NavLink>
              </>
            )}

            {/* Menu utilisateur */}
            <div style={{ position: 'relative', marginLeft: '10px' }}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <User size={16} />

                <span style={{ fontSize: '14px' }}>{user.email?.split('@')[0]}</span>
                <span style={{ fontSize: '10px' }}>▼</span>
              </button>

              {menuOpen && (
                <div style={{
                  position: 'absolute',
                  top: '45px',
                  right: 0,
                  background: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  minWidth: '240px',
                  overflow: 'hidden'
                }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>Connecté en tant que</div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333' }}>
                      {userType === 'prof' ? 'Professeur' : 'Élève'}
                    </div>
                  </div>

                  {/* Gestion / résiliation de l'abonnement (placée ici pour éviter les clics accidentels) */}
                  {userType === 'eleve' && creditsData?.has_subscription && creditsData?.abonnement?.type !== 'essai' && (
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee' }}>
                      <ResilierAbonnement
                        token={token}
                        abonnement={creditsData.abonnement}
                        onDone={() => loadCredits(token)}
                      />
                    </div>
                  )}

                  {userType === 'eleve' && creditsData?.has_subscription && creditsData?.abonnement?.type !== 'essai' && (
                    <button
                      onClick={openBillingPortal}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        borderBottom: '1px solid #eee',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: '#374151',
                        fontSize: '14px'
                      }}
                    >
                      Carte &amp; factures
                    </button>
                  )}

                  <button
                    onClick={handleLogout}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      color: '#ef4444',
                      fontSize: '14px'
                    }}
                  >
                    🚪 Déconnexion
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <NavLink to="/professeurs" active={isActive('/professeurs')}>
              Nos professeurs
            </NavLink>
            <Link
              to="/login"
              style={{
                background: 'white',
                color: '#064e3b',
                padding: '8px 16px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
            >
              Connexion
            </Link>
          </>
        )}
      </nav>
    </header>
  );
};

// Composant NavLink stylisé
const NavLink = ({ to, active, children }) => (
  <Link
    to={to}
    style={{
      color: 'white',
      textDecoration: 'none',
      padding: '8px 14px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: active ? 'bold' : 'normal',
      background: active ? 'rgba(255,255,255,0.15)' : 'transparent',
      transition: 'background 0.2s'
    }}
  >
    {children}
  </Link>
);

export default Header;
